import { NavLink, useNavigate } from "react-router-dom";
import {
  ReceiptText, ShieldCheck, Blocks, MessagesSquare,
  Settings, ChevronsUpDown, UsersRound,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ariaLogo from "@/assets/aria-logo.png";
import { AriaMark } from "@/components/AriaMark";

const NAV = [
  { to: "/communications", label: "Communications", icon: MessagesSquare, id: "communications" },
  { to: "/expenses", label: "Expenses", icon: ReceiptText, id: "expenses" },
  { to: "/policies", label: "Policies", icon: ShieldCheck, id: "policies" },
];

const SECONDARY = [
  { to: "/integrations", label: "Integrations", icon: Blocks, id: "integrations" },
  { to: "/settings", label: "Settings", icon: Settings, id: "settings" },
];

const navClass = ({ isActive }) =>
  `group nav-item flex items-center gap-3 rounded-xl px-3 py-[7px] text-[13px] ${
    isActive
      ? "bg-[#1c1c1c] text-white"
      : "text-[#A3A3A3] hover:bg-[#131313] hover:text-white"
  }`;

export const LeftSidebar = () => {
  const navigate = useNavigate();
  return (
    <aside className="fixed bottom-0 top-9 left-0 z-30 hidden w-[248px] flex-col border-r border-[#222222] bg-[#0A0A0A] lg:flex">
      {/* logo */}
      <div className="flex h-16 items-center gap-2.5 px-5">
        <img src={ariaLogo} alt="Aria" className="h-8 w-8 rounded-[10px]" data-testid="aria-logo" />
        <div className="text-[15px] font-semibold leading-none tracking-tight">Aria</div>
      </div>

      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 pt-2" data-testid="sidebar-nav">
        <button onClick={() => window.dispatchEvent(new Event("aria:open"))} data-testid="sidebar-nav-aria"
          className="group nav-item flex w-full items-center gap-3 rounded-xl px-3 py-[7px] text-[13px] text-[#A3A3A3] hover:bg-[#131313] hover:text-white">
          <AriaMark size={17} />
          <span className="flex-1 text-left">Aria</span>
          <span className="h-1.5 w-1.5 rounded-full bg-[#22C55E] pulse-dot" title="Aria online" />
        </button>
        {NAV.map((item) => (
          <NavLink key={item.id} to={item.to} data-testid={`sidebar-nav-${item.id}`} className={navClass}>
            <item.icon size={17} />
            <span className="flex-1">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* bottom */}
      <div className="border-t border-[#222222] p-3">
        {/* Integrations + Settings — sit just above the workspace */}
        <div className="space-y-0.5" data-testid="sidebar-secondary-nav">
          {SECONDARY.map((item) => (
            <NavLink key={item.id} to={item.to} data-testid={`sidebar-nav-${item.id}`} className={navClass}>
              <item.icon size={17} />
              <span className="flex-1">{item.label}</span>
            </NavLink>
          ))}
        </div>

        <div className="my-2.5 h-px bg-[#1c1c1c]" />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="workspace-switcher"
              className="flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left hover:bg-[#111111]"
            >
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#1c1c1c] text-[11px] font-semibold text-white border border-[#2a2a2a]">
                AC
              </div>
              <div className="flex-1 min-w-0">
                <div className="truncate text-[12px] font-medium text-white">Acme Corp</div>
                <div className="text-[10px] text-[#A3A3A3]">Production workspace</div>
              </div>
              <ChevronsUpDown size={14} className="text-[#6b6b6b]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-56 border-[#222] bg-[#111] text-white">
            <DropdownMenuLabel className="text-[#A3A3A3]">Workspaces</DropdownMenuLabel>
            <DropdownMenuItem className="focus:bg-[#1c1c1c] focus:text-white">Acme Corp — Production</DropdownMenuItem>
            <DropdownMenuItem className="focus:bg-[#1c1c1c] focus:text-white">Acme Corp — Sandbox</DropdownMenuItem>
            <DropdownMenuSeparator className="bg-[#222]" />
            <DropdownMenuItem data-testid="workspace-team-row" onSelect={() => navigate("/settings?tab=team")}
              className="gap-2 focus:bg-[#1c1c1c] focus:text-white">
              <UsersRound size={14} className="text-[#7FAEF9]" /> Team
            </DropdownMenuItem>
            <DropdownMenuItem className="focus:bg-[#1c1c1c] focus:text-white">Create workspace</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
};
